import { SalesSummary, SalesSummaryRow } from '@/lib/api/financial/financial-dashboard-api'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { MdHelpOutline, MdInfoOutline, MdShoppingCart, MdWarning } from 'react-icons/md'

interface SalesSummaryTableProps {
    summary: SalesSummary
    onHelp?: () => void
    /**
     * Torna o "A receber" clicável (o Fechamento do mês abre a lista do que está em
     * aberto daquele tipo, ou de todos no Total). Sem ele, a célula é só o número.
     */
    onOutstandingClick?: (type: SalesSummaryRow['type']) => void
}

const formatCurrency = (cents: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100)

const formatShortDate = (iso: string) =>
    new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(new Date(iso))

/**
 * "O que foi vendido": por tipo de atendimento, o total VENDIDO no período (pela data
 * da venda) e quanto dessa venda já entrou — até o fim do período e até hoje.
 *
 * Responde a pergunta que os cards de caixa não respondem: um flex de R$ 23.000 que
 * recebeu a entrada aparece como vendido R$ 23.000 e recebido R$ 3.000, e o que falta
 * fica à vista. De propósito simples: uma tabela, sem gráfico.
 */
export default function SalesSummaryTable({ summary, onHelp, onOutstandingClick }: SalesSummaryTableProps) {
    const labels = useServiceLabel()
    const rowLabel: Record<SalesSummaryRow['type'], string> = {
        CONSULTATION: labels.Consultations,
        EXAM: labels.Exams,
        ORDER: labels.Orders,
        TOTAL: 'Total',
    }

    const periodEnd = new Date(summary.periodEnd)
    // Período que termina hoje ou depois: "até o fim do período" e "até hoje" são o
    // mesmo número, e a segunda coluna só repetiria a primeira.
    const periodIsOver = periodEnd.getTime() < Date.now()
    const healthPlanCount = summary.total.healthPlanCount
    const truncated = summary.total.truncated

    const rows = [...summary.rows, summary.total]

    return (
        <div className="bg-base-100 rounded-2xl border border-base-300/60 p-5 shadow-sm">
            <div className="flex items-start gap-3 mb-4">
                <div className="p-2 rounded-xl bg-secondary/10 text-secondary">
                    <MdShoppingCart className="text-xl" />
                </div>
                <div className="flex-1">
                    <h2 className="font-semibold text-base-content">O que foi vendido</h2>
                    <p className="text-sm text-base-content/60">
                        Atendimentos vendidos no período (data da venda) e quanto deles já entrou.
                        Orçamentos e cancelados não contam. O vendido é líquido da taxa do gateway, o
                        mesmo número do bloco "Por tipo de atendimento".
                    </p>
                </div>
                {onHelp && (
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs btn-circle text-base-content/40 hover:text-base-content"
                        onClick={onHelp}
                        aria-label="Como ler o que foi vendido"
                        title="Como ler o que foi vendido"
                    >
                        <MdHelpOutline className="text-base" />
                    </button>
                )}
            </div>

            <div className="overflow-x-auto">
                <table className="table table-sm">
                    <thead>
                        <tr className="text-base-content/60">
                            <th>Tipo</th>
                            <th className="text-right">Atendimentos</th>
                            <th className="text-right">Vendido</th>
                            {periodIsOver && (
                                <th className="text-right">Recebido até {formatShortDate(summary.periodEnd)}</th>
                            )}
                            <th className="text-right">Recebido</th>
                            <th className="text-right">A receber</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => {
                            const isTotal = row.type === 'TOTAL'
                            return (
                                <tr
                                    key={row.type}
                                    className={isTotal ? 'font-semibold border-t-2 border-base-300' : ''}
                                >
                                    <td>{rowLabel[row.type]}</td>
                                    <td className="text-right tabular-nums">{row.count.toLocaleString('pt-BR')}</td>
                                    <td className="text-right tabular-nums">{formatCurrency(row.sold)}</td>
                                    {periodIsOver && (
                                        <td className="text-right tabular-nums">{formatCurrency(row.receivedUntilEnd)}</td>
                                    )}
                                    <td className="text-right tabular-nums text-success">
                                        {formatCurrency(row.receivedToDate)}
                                    </td>
                                    <td
                                        className={`text-right tabular-nums ${row.outstanding > 0 ? 'text-warning' : 'text-base-content/50'}`}
                                    >
                                        {onOutstandingClick && row.outstanding > 0 ? (
                                            <button
                                                type="button"
                                                className="link link-hover tabular-nums"
                                                title="Ver o que está em aberto"
                                                onClick={() => onOutstandingClick(row.type)}
                                            >
                                                {formatCurrency(row.outstanding)}
                                            </button>
                                        ) : (
                                            formatCurrency(Math.max(0, row.outstanding))
                                        )}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>

            {(healthPlanCount > 0 || truncated) && (
                <div className="mt-3 flex flex-col gap-1 text-xs text-base-content/60">
                    {healthPlanCount > 0 && (
                        <p className="flex items-center gap-1">
                            <MdInfoOutline className="shrink-0" />
                            Inclui {healthPlanCount.toLocaleString('pt-BR')} atendimento(s) de convênio (
                            {formatCurrency(summary.total.healthPlanSold)}): sem custo para o paciente, cobrados na
                            fatura do convênio. Entram como venda agora e como recebidos quando a fatura for paga.
                        </p>
                    )}
                    {truncated && (
                        <p className="flex items-center gap-1 text-warning">
                            <MdWarning className="shrink-0" />
                            Período muito longo: os números cobrem só parte dos atendimentos. Reduza o período.
                        </p>
                    )}
                </div>
            )}
        </div>
    )
}
